// features/intelligence-feature.js — INTELLIGENCE TAB
// ═══════════════════════════════════════════════════════════
// Unified intelligence feed. Replaces separate Lens + Depth tabs.
// Read-only report cards (no conversation). Deep-mode-style cards.
//
// Features:
//   - Search bar (full-text across title, body, framework)
//   - Type filter (All / Lens / Depth)
//   - Time filter (All time / This week / This month)
//   - Select mode with bulk delete
//   - "Discuss →" sends finding title to main chat
//   - Grouped by Today / This week / Earlier
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import MessageContent from '../components/message-content';
import DepthCard from '../components/features/depth/DepthCard';
import LensCard from '../components/features/intelligence/LensCard';

// ── Colors ──
const C = {
  gold: '#c8a96e', sage: '#7a9e82', purple: '#9b8fc4', terra: '#c46050',
  text1: '#d4d4d4', text2: '#a8a8a8', text3: '#888', text4: '#666', text5: '#444',
  bg0: '#07070a', bg1: '#07070a', bg2: '#07070a', bg3: '#141414',
  border1: '#1e1e2a', border2: '#272736', border3: '#2a2a2a',
};

const FONT = 'Georgia, serif';

// Framework type → color for lens findings
const LENS_COLORS = {
  pattern: C.sage, convergence: C.sage, causal_chain: C.sage,
  gap: C.terra, contradiction: C.terra, hidden_assumption: C.terra,
  binding_constraint: C.gold, cross_domain: C.purple,
  indicator: C.sage, structural_insight: C.purple, structural_trap: C.terra,
  breakthrough: C.gold, decision: C.gold, reframe: C.purple,
  idea_worth_keeping: C.gold, key_question: C.purple, next_experiment: C.sage,
};

function timeAgo(dateStr) {
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function groupByTime(reports) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart); weekStart.setDate(weekStart.getDate() - 7);

  const today = [], thisWeek = [], earlier = [];
  for (const r of reports) {
    const d = new Date(r.created_at);
    if (d >= todayStart) today.push(r);
    else if (d >= weekStart) thisWeek.push(r);
    else earlier.push(r);
  }
  return { today, thisWeek, earlier };
}

// ── Intelligence Card ──
function IntelligenceCard({ report, selectMode, selected, onToggle, onDiscuss, userRecord }) {
  const isLens = report.type === 'lens';
  const color = isLens ? (LENS_COLORS[report.framework] || C.sage) : C.gold;
  const typeLabel = isLens ? report.framework.replace(/_/g, ' ') : 'DEPTH';

  // Try to parse lens reports as structured JSON for LensCard
  if (isLens) {
    let structured = null;
    try {
      const body = (report.body || '').trim();
      if (body.startsWith('{')) structured = JSON.parse(body);
    } catch { /* not structured */ }

    if (structured) {
      return (
        <div style={{ position: 'relative' }}>
          {selectMode && (
            <button
              onClick={() => onToggle?.(report.id)}
              style={{
                position: 'absolute', top: 10, right: 10, zIndex: 5,
                width: 18, height: 18, borderRadius: 4,
                border: `1px solid ${selected ? C.gold : '#2a2a2a'}`,
                background: selected ? 'rgba(200,169,110,0.12)' : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: selected ? C.gold : 'transparent',
              }}
            >
              {selected ? '✓' : ''}
            </button>
          )}
          <LensCard
            card={structured}
            index={0}
            onDiscuss={() => onDiscuss?.(report)}
          />
        </div>
      );
    }
  }

  // Try to parse depth reports as structured JSON for DepthCard
  if (!isLens) {
    let structured = null;
    try {
      const body = (report.body || '').trim();
      if (body.startsWith('{')) structured = JSON.parse(body);
    } catch { /* not structured, fall through to plain card */ }

    if (structured) {
      return (
        <div style={{ position: 'relative' }}>
          {selectMode && (
            <button
              onClick={() => onToggle?.(report.id)}
              style={{
                position: 'absolute', top: 10, right: 10, zIndex: 5,
                width: 18, height: 18, borderRadius: 4,
                border: `1px solid ${selected ? C.gold : '#2a2a2a'}`,
                background: selected ? 'rgba(200,169,110,0.12)' : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: selected ? C.gold : 'transparent',
              }}
            >
              {selected ? '✓' : ''}
            </button>
          )}
          <DepthCard
            data={structured}
            onAskShift={() => onDiscuss?.(report)}
          />
        </div>
      );
    }
  }

  return (
    <div style={{
      background: C.bg1,
      border: `1px solid ${selected ? `${C.terra}55` : C.border1}`,
      borderRadius: 10,
      overflow: 'hidden',
      transition: 'border-color 0.15s',
    }}>
      {/* Colored accent bar at top */}
      <div style={{ height: 3, background: color, opacity: 0.6 }} />

      <div style={{ padding: '28px 40px 24px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          {selectMode && (
            <button
              onClick={() => onToggle?.(report.id)}
              style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                border: `1px solid ${selected ? C.gold : C.border3}`,
                background: selected ? 'rgba(200,169,110,0.12)' : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: selected ? C.gold : 'transparent',
                transition: 'all 0.12s',
              }}
            >
              {selected ? '✓' : ''}
            </button>
          )}
          {/* Type pill */}
          <span style={{
            fontSize: 8, fontFamily: FONT, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color, padding: '2px 8px', borderRadius: 10,
            background: `${color}12`, border: `1px solid ${color}33`,
          }}>
            {typeLabel}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 9, color: C.text5, fontFamily: FONT, fontStyle: 'italic' }}>
            {timeAgo(report.created_at)}
          </span>
        </div>

        {/* Title — larger, bolder */}
        <div style={{
          fontSize: 15, fontFamily: FONT, fontWeight: 700,
          color: C.text1, lineHeight: 1.35, marginBottom: 4,
          letterSpacing: '-0.01em',
        }}>
          {report.title}
        </div>

        {/* Subtitle for depth frames */}
        {!isLens && report.framework && (
          <div style={{
            fontSize: 10, fontFamily: FONT, fontStyle: 'italic',
            color: C.text4, marginBottom: 10,
          }}>
            {report.framework}
          </div>
        )}

        {/* Divider */}
        <div style={{ height: 1, background: C.border1, margin: '10px 0 12px' }} />

        {/* Body — full content, good reading typography */}
        <div style={{
          fontSize: 13, fontFamily: FONT, color: C.text2,
          lineHeight: 1.8, letterSpacing: '0.005em',
        }}>
          <MessageContent m={{ role: 'assistant', content: report.body }} i={0} />
        </div>

        {/* Footer — Discuss button */}
        {!selectMode && (
          <div style={{ display: 'flex', gap: 6, marginTop: 14, paddingTop: 10, borderTop: `1px solid ${C.border1}` }}>
            <button
              onClick={() => onDiscuss?.(report)}
              style={{
                fontSize: 9, fontFamily: FONT, color: C.sage,
                background: 'rgba(122,158,130,0.08)',
                border: '1px solid rgba(122,158,130,0.25)',
                borderRadius: 4, padding: '4px 12px', cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(122,158,130,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(122,158,130,0.08)'; }}
            >
              Discuss →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section group ──
function Section({ label, reports, selectMode, selectedIds, onToggle, onDiscuss }) {
  if (!reports || reports.length === 0) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 9, fontFamily: FONT, color: C.text5,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        marginBottom: 12, paddingBottom: 4,
        borderBottom: `1px solid ${C.border1}`,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {reports.map(r => (
          <IntelligenceCard
            key={r.id}
            report={r}
            selectMode={selectMode}
            selected={selectedIds.has(r.id)}
            onToggle={onToggle}
            onDiscuss={onDiscuss}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main component ──
export default function IntelligenceFeature({ userId, supabase, userRecord, workspaceId, onDiscuss }) {
  const aiName = userRecord?.ai_name || 'Shift';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Load reports ──
  const loadReports = useCallback(async () => {
    if (!userId || !supabase) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const params = new URLSearchParams();
      if (workspaceId) params.set('workspaceId', workspaceId);
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (search.trim()) params.set('search', search.trim());
      if (timeFilter === 'week') {
        params.set('since', new Date(Date.now() - 7 * 86400000).toISOString());
      } else if (timeFilter === 'month') {
        params.set('since', new Date(Date.now() - 30 * 86400000).toISOString());
      }

      const res = await fetch(`/api/workspace/intelligence-reports?${params}`, {
        headers: { 'Authorization': `Bearer ${session?.access_token || ''}` },
      });
      if (res.ok) {
        const { reports: data } = await res.json();
        setReports(data || []);
      }
    } catch (e) {
      console.error('[intelligence] load failed:', e.message);
    } finally {
      setLoading(false);
    }
  }, [userId, supabase, workspaceId, typeFilter, timeFilter, search]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // ── Delete selected ──
  const handleDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} report${selectedIds.size > 1 ? 's' : ''}?`)) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch('/api/workspace/intelligence-reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({ action: 'delete', ids: [...selectedIds] }),
      });
      setSelectedIds(new Set());
      setSelectMode(false);
      loadReports();
    } catch (e) {
      console.error('[intelligence] delete failed:', e.message);
    }
  }, [selectedIds, supabase, loadReports]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(reports.map(r => r.id)));
  }, [reports]);

  const grouped = useMemo(() => groupByTime(reports), [reports]);

  // ── Empty state ──
  if (!loading && reports.length === 0 && !search) {
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: 40,
      }}>
        <div style={{ fontSize: 14, fontFamily: FONT, color: C.text3, textAlign: 'center', lineHeight: 1.8 }}>
          Intelligence reports will appear here as you use your workspaces.
        </div>
        <div style={{ fontSize: 11, fontFamily: FONT, color: C.text5, textAlign: 'center', marginTop: 8, lineHeight: 1.6 }}>
          {aiName} picks 1-2 analytical frameworks each day based on your conversations and generates focused insights. Check back after a few sessions.
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg0, overflow: 'hidden', minHeight: 0 }}>
      {/* Control row: search (half width) + spacer + delete + select + type */}
      <div style={{
        padding: '8px 20px', borderBottom: `1px solid ${C.border1}`,
        display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0,
      }}>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: 140, background: C.bg3, border: `1px solid ${C.border1}`,
            borderRadius: 5, padding: '5px 8px', fontSize: 10,
            fontFamily: FONT, color: C.text1, outline: 'none',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1 }} />
        {/* Delete — always in this position, only visible when items selected */}
        {selectedIds.size > 0 && (
          <button onClick={handleDelete} style={{
            fontSize: 9, fontFamily: FONT, color: C.terra,
            background: `${C.terra}11`, border: `1px solid ${C.terra}33`,
            borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
          }}>
            Delete {selectedIds.size}
          </button>
        )}
        {/* Select toggle — neutral static, sage hover, gold active */}
        <button
          onClick={() => {
            setSelectMode(v => !v);
            if (selectMode) setSelectedIds(new Set());
          }}
          style={{
            fontSize: 9, fontFamily: FONT,
            color: selectMode ? C.gold : C.text4,
            background: selectMode ? 'rgba(200,169,110,0.08)' : 'none',
            border: `1px solid ${selectMode ? 'rgba(200,169,110,0.25)' : C.border1}`,
            borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            if (!selectMode) {
              e.currentTarget.style.color = '#7a9e82';
              e.currentTarget.style.borderColor = 'rgba(122,158,130,0.25)';
            }
          }}
          onMouseLeave={e => {
            if (!selectMode) {
              e.currentTarget.style.color = C.text4;
              e.currentTarget.style.borderColor = C.border1;
            }
          }}
        >
          Select
        </button>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          style={{
            background: C.bg3, border: `1px solid ${C.border1}`,
            borderRadius: 4, padding: '4px 6px', fontSize: 9,
            fontFamily: FONT, color: C.text3, cursor: 'pointer',
          }}
        >
          <option value="all">All</option>
          <option value="lens">Lens</option>
          <option value="depth">Depth</option>
        </select>
      </div>

      {/* Report feed — scrollable container */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}>
        {loading ? (
          <div style={{ fontSize: 11, fontFamily: FONT, color: C.text5, textAlign: 'center', padding: 40 }}>
            Loading...
          </div>
        ) : reports.length === 0 ? (
          <div style={{ fontSize: 11, fontFamily: FONT, color: C.text5, textAlign: 'center', padding: 40 }}>
            No results for &ldquo;{search}&rdquo;
          </div>
        ) : (
          <>
            <Section label="Today" reports={grouped.today} selectMode={selectMode} selectedIds={selectedIds} onToggle={toggleSelect} onDiscuss={onDiscuss} />
            <Section label="This week" reports={grouped.thisWeek} selectMode={selectMode} selectedIds={selectedIds} onToggle={toggleSelect} onDiscuss={onDiscuss} />
            <Section label="Earlier" reports={grouped.earlier} selectMode={selectMode} selectedIds={selectedIds} onToggle={toggleSelect} onDiscuss={onDiscuss} />
          </>
        )}
      </div>
    </div>
  );
}
